import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Loader2, ExternalLink, ShieldAlert, ArrowLeft, Workflow, Plus, Search, Check, ChevronsUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { PageTransition } from '@/components/PageTransition'
import { EngineStatusBanner } from '@/components/cadences/EngineStatusBanner'
import { useCadence } from '@/contexts/CadenceContext'
import { useOutreachEngineStatus } from '@/hooks/useOutreachEngineStatus'
import { usePermissions } from '@/hooks/usePermissions'
import { PolicySection } from '@/components/autom-cadence/PolicySection'
import { AnglesSection } from '@/components/autom-cadence/AnglesSection'
import { LimitsSection } from '@/components/autom-cadence/LimitsSection'
import { SummarySection } from '@/components/autom-cadence/SummarySection'
import { StepsSection } from '@/components/autom-cadence/StepsSection'
import { stepGeneratesMessage } from '@/lib/cadence-step-predicates'

// La cadencia 9-day canónica: el default "de verdad" al que debe aterrizar
// /autom-cadence (sin :id). Se prioriza por id explícito para no depender de
// heurísticas cuando esta cadencia existe en la lista del owner.
const CANONICAL_9DAY_CADENCE_ID = '48c6b5bc-0276-47bb-a275-5fa8beaa6c30'

const VALID_TABS = ['summary', 'steps', 'policy', 'angles', 'limits'] as const
type TabKey = (typeof VALID_TABS)[number]

function isTabKey(v: string | null): v is TabKey {
  return v !== null && (VALID_TABS as readonly string[]).includes(v)
}

// ────────────────────────────────────────────────────────────────────────────
// AutomCadence — the single module where the owner configures the full outreach
// pipeline of ANY cadence: steps, Carlos policy, per-touch angles and operational
// limits. Everything is scoped by the cadence selected in the persistent header.
// Tab state lives in ?tab= (CadenceBuilder pattern); the outreach_admin guard is
// applied ONCE here for the whole module.
// ────────────────────────────────────────────────────────────────────────────
export function AutomCadence() {
  const { id: idFromUrl } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { cadences, isLoading } = useCadence()
  const { outreach_admin } = usePermissions()
  const { engineOff } = useOutreachEngineStatus()

  // Searchable cadence selector (owners can have up to ~119 cadences — a plain
  // <Select> gives no way to filter). Built from Popover + Input + a scrollable
  // list of buttons using ONLY existing primitives (no cmdk/Combobox lib).
  const [selectorOpen, setSelectorOpen] = useState(false)
  const [selectorQuery, setSelectorQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  const activeTab: TabKey = isTabKey(searchParams.get('tab')) ? (searchParams.get('tab') as TabKey) : 'summary'
  const setActiveTab = (next: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.set('tab', next)
      return params
    })
  }

  // Default cadence (criterio, para bookmarks viejos que aterrizan en /autom-cadence
  // sin :id): queremos aterrizar en una cadencia REAL/canónica, nunca en una de
  // test vacía (p.ej. 'za'). Prioridad, en orden:
  //   (1) La 9-day CANÓNICA por id explícito, si está en la lista del owner.
  //   (2) Si no, la cadencia ACTIVE con MÁS pasos de mensaje (contados con
  //       stepGeneratesMessage): una cadencia real con contenido gana a un test
  //       vacío. Empates se rompen por created_at ASC (la más antigua/estable).
  //   (3) Si nadie tiene pasos de mensaje (todas empatan en 0) o no hay señal,
  //       la PRIMERA active por created_at ASC.
  //   (4) Si no hay ninguna active, la más reciente: cadences[0], ya que el
  //       contexto entrega la lista ordenada por created_at DESC.
  const defaultId = useMemo(() => {
    if (cadences.length === 0) return null

    // (1) 9-day canónica por id explícito.
    const canonical = cadences.find((c) => c.id === CANONICAL_9DAY_CADENCE_ID)
    if (canonical) return canonical.id

    const actives = cadences.filter((c) => c.status === 'active')

    // (2) ACTIVE con más pasos de mensaje; desempate por created_at ASC.
    const messageStepCount = (c: (typeof cadences)[number]) =>
      (c.steps ?? []).filter((s) => stepGeneratesMessage(s)).length
    const byContentThenAge = [...actives].sort((a, b) => {
      const diff = messageStepCount(b) - messageStepCount(a)
      if (diff !== 0) return diff
      const aT = new Date(a.created_at ?? 0).getTime()
      const bT = new Date(b.created_at ?? 0).getTime()
      return aT - bT
    })
    const richestActive = byContentThenAge[0]
    if (richestActive && messageStepCount(richestActive) > 0) return richestActive.id

    // (3) Empate/none en pasos de mensaje: primera active por created_at ASC.
    //     `byContentThenAge` ya está ordenado por edad ASC dentro del empate en 0.
    if (byContentThenAge.length > 0) return byContentThenAge[0].id

    // (4) Sin ninguna active: la más reciente (el contexto ya ordena created_at DESC).
    return cadences[0].id
  }, [cadences])

  const activeId = idFromUrl ?? defaultId

  // /autom-cadence (no :id) → redirect to the stable default cadence
  // (9-day canónica → active con más pasos de mensaje → primera active → más
  // reciente; ver defaultId), keeping ?tab=.
  useEffect(() => {
    if (!idFromUrl && defaultId) {
      const tab = searchParams.get('tab')
      const qs = tab ? `?tab=${tab}` : ''
      navigate(`/autom-cadence/${defaultId}${qs}`, { replace: true })
    }
  }, [idFromUrl, defaultId, navigate, searchParams])

  // ── Access guard (applied once for the whole module) ────────────────────────
  if (!outreach_admin) {
    return (
      <PageTransition className="p-8">
        <div className="max-w-md mx-auto text-center py-16">
          <ShieldAlert className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h1 className="text-lg font-semibold">Acceso restringido</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Solo un administrador puede configurar la automatización de cadencias.
          </p>
          <Button asChild variant="outline" size="sm" className="mt-4">
            <Link to="/settings"><ArrowLeft className="h-4 w-4 mr-1" />Volver a Settings</Link>
          </Button>
        </div>
      </PageTransition>
    )
  }

  // Distinguish "still loading" from "genuinely empty".
  if (isLoading && cadences.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (cadences.length === 0) {
    return (
      <PageTransition className="p-8">
        <Card className="border-dashed p-12 text-center max-w-lg mx-auto">
          <Workflow className="mx-auto size-10 text-muted-foreground" />
          <h2 className="mt-3 text-lg font-semibold">Aún no tienes cadencias</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Crea una cadencia para configurar su pipeline de outreach automatizado aquí.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <Button onClick={() => navigate('/autom-cadence/new')}>
              <Plus className="mr-1 size-4" />
              Nueva cadencia
            </Button>
            <Button variant="outline" onClick={() => navigate('/cadences')}>
              Ir a Cadences
            </Button>
          </div>
        </Card>
      </PageTransition>
    )
  }

  if (!activeId) return null

  // Resolve the active cadence WITHOUT a silent fallback to cadences[0]:
  // `cadences` is owner-filtered, so an admin opening /autom-cadence/:id with an
  // id that isn't theirs / was deleted / is stale must NOT be quietly redirected
  // onto the first cadence while the URL + Select keep showing the requested id
  // — that desync means the user thinks they're editing cadence X but actually
  // saves rubrics/angles into cadence Y. The cadences[0] fallback is ONLY valid
  // when there is no :id in the URL (activeId came from defaultId).
  const found = cadences.find((c) => c.id === activeId)

  if (idFromUrl && !found) {
    // :id present but no match. `isLoading` is already handled above (the loader
    // returns early while cadences is empty); reaching here with a non-empty list
    // means the id genuinely doesn't belong to this user or no longer exists.
    return (
      <PageTransition className="p-8">
        <Card className="border-dashed p-12 text-center max-w-lg mx-auto">
          <ShieldAlert className="mx-auto size-10 text-muted-foreground" />
          <h2 className="mt-3 text-lg font-semibold">Cadencia no encontrada o no te pertenece</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            No pudimos abrir esta cadencia. Puede que haya sido eliminada o que pertenezca
            a otro usuario. Elige una de tus cadencias para configurar su automatización.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <Button onClick={() => navigate('/autom-cadence', { replace: true })}>
              <ArrowLeft className="mr-1 size-4" />
              Ir a mis cadencias
            </Button>
            <Button variant="outline" onClick={() => navigate('/cadences')}>
              Ir a Cadences
            </Button>
          </div>
        </Card>
      </PageTransition>
    )
  }

  // Normal path: id valid → found. When there is no :id (activeId came from
  // defaultId), `found` resolves the default cadence; the redirect effect above
  // keeps the URL in sync, so cadences[0] is never silently substituted.
  const active = found ?? cadences[0]

  // Navigate to a cadence preserving the current ?tab= (same shape as the old
  // <Select> onValueChange). Also closes + resets the searchable selector.
  const goToCadence = (id: string) => {
    const tab = searchParams.get('tab')
    setSelectorOpen(false)
    setSelectorQuery('')
    navigate(`/autom-cadence/${id}${tab ? `?tab=${tab}` : ''}`)
  }

  // Filter by name, case-insensitive + accent-tolerant-ish (simple lowercase).
  const query = selectorQuery.trim().toLowerCase()
  const filteredCadences = query
    ? cadences.filter((c) => c.name.toLowerCase().includes(query))
    : cadences

  return (
    <PageTransition className="p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* ── Persistent header ──────────────────────────────────────────── */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">Autom Cadence</h1>
            <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
              Configura, para cualquier cadencia, todo el pipeline de outreach automatizado —
              pasos, prompt, política de Carlos, ángulos por touch y límites operativos — para
              traer leads enterprise calificados.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Popover
              open={selectorOpen}
              onOpenChange={(next) => {
                setSelectorOpen(next)
                // Reset the filter each time it closes so it reopens clean.
                if (!next) setSelectorQuery('')
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={selectorOpen}
                  aria-label="Seleccionar cadencia"
                  className="w-[260px] justify-between font-normal"
                >
                  <span className="truncate">{active.name}</span>
                  <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[260px] p-0"
                align="start"
                // Focus the search Input when the popover opens (keyboard a11y).
                onOpenAutoFocus={(e) => {
                  e.preventDefault()
                  searchInputRef.current?.focus()
                }}
              >
                <div className="border-b p-2">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      ref={searchInputRef}
                      value={selectorQuery}
                      onChange={(e) => setSelectorQuery(e.target.value)}
                      placeholder="Buscar cadencia…"
                      className="h-8 pl-8"
                      aria-label="Buscar cadencia por nombre"
                    />
                  </div>
                </div>
                <div className="max-h-[300px] overflow-y-auto p-1">
                  {filteredCadences.length === 0 ? (
                    <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                      Sin resultados
                    </p>
                  ) : (
                    filteredCadences.map((c) => {
                      const isActive = c.id === active.id
                      return (
                        <button
                          key={c.id}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          onClick={() => goToCadence(c.id)}
                          className={cn(
                            'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none',
                            'hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground',
                            isActive && 'font-medium',
                          )}
                        >
                          <Check
                            className={cn(
                              'size-4 shrink-0',
                              isActive ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          <span className="truncate">{c.name}</span>
                        </button>
                      )
                    })
                  )}
                </div>
              </PopoverContent>
            </Popover>
            {active.status === 'active' && engineOff ? (
              // Cadencia activa PERO motor de outreach apagado: no dejar que "activa"
              // se lea como "está enviando". Se matiza el badge (variant secondary,
              // no default) y un tooltip remite al banner de estado del motor abajo.
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="secondary"
                      className="uppercase text-[10px] tracking-wide cursor-help"
                    >
                      activa · motor apagado
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[220px] text-center normal-case">
                    La cadencia está activa, pero el motor de outreach está apagado:
                    los pasos programados no se envían. Ver el banner de estado del motor.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Badge
                variant={active.status === 'active' ? 'default' : 'secondary'}
                className="uppercase text-[10px] tracking-wide"
              >
                {active.status === 'active' ? 'activa' : 'borrador'}
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/cadences/${active.id}`)}
            >
              <ExternalLink className="mr-1 size-4" />
              Abrir builder
            </Button>
            <Button
              size="sm"
              onClick={() => navigate('/autom-cadence/new')}
            >
              <Plus className="mr-1 size-4" />
              Nueva cadencia
            </Button>
          </div>
        </div>

        {/* Honest engine banner — scheduled steps don't run while the engine is off */}
        <EngineStatusBanner />

        {/* ── Tabs (state in ?tab=) ──────────────────────────────────────── */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="summary">Resumen</TabsTrigger>
            <TabsTrigger value="steps">Pasos</TabsTrigger>
            <TabsTrigger value="policy">Política</TabsTrigger>
            <TabsTrigger value="angles">Ángulos</TabsTrigger>
            <TabsTrigger value="limits">Límites</TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="mt-6">
            <SummarySection cadenceId={active.id} onGoTab={setActiveTab} />
          </TabsContent>

          <TabsContent value="steps" className="mt-6">
            <StepsSection cadenceId={active.id} onGoTab={setActiveTab} />
          </TabsContent>

          <TabsContent value="policy" className="mt-6">
            <PolicySection cadenceId={active.id} />
          </TabsContent>

          <TabsContent value="angles" className="mt-6">
            <AnglesSection cadenceId={active.id} />
          </TabsContent>

          <TabsContent value="limits" className="mt-6">
            <LimitsSection cadenceId={active.id} />
          </TabsContent>
        </Tabs>
      </div>
    </PageTransition>
  )
}
